import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Fade
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Visibility, 
  VisibilityOff 
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';

// --- CAROUSEL DATA ---
const carouselSteps = [
  {
    title: "Transform Your Home",
    description: "Get professional Scopes of Work, compare verified builder quotes, and manage your project with confidence."
  },
  {
    title: "Avoid Costly Mistakes",
    description: "Our AI-driven planning tools help you define every detail before you build, saving you time and money on unexpected extras."
  },
  {
    title: "Invite Your Trusted Builders",
    description: "Share your professional scope of work with your preferred builders to get accurate, comparable quotes."
  }
];

const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { login, isAuthenticated, loading, user } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Carousel State
  const [activeStep, setActiveStep] = useState(0);

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % carouselSteps.length);
    }, 5000); // Change every 5 seconds
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      if (user.userType === 'builder') {
        navigate('/app/builder/dashboard', { replace: true });
      } else {
        navigate('/app/projects', { replace: true });
      }
    }
  }, [isAuthenticated, loading, user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');
    try {
      await login(data.email, data.password);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      
      {/* --- LEFT SIDE: FORM --- */}
      <Grid
        item
        xs={12}
        sm={8}
        md={5}
        component={Paper}
        elevation={6}
        square
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: { xs: 3, md: 4 }, // Less padding on mobile (24px), more on desktop (32px)
          overflowY: 'auto'    // Safety: Allows scrolling on landscape mobile screens
        }}
      >
        {/* 1. Back Button */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button 
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />} 
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Back to Home
          </Button>
        </Box>

        {/* 2. Centered Form Container */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%'
        }}>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            
            {/* Branding Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'center' }}>
              <Box 
                component="img" 
                src="/logo.png" 
                alt="Quorite Logo" 
                sx={{ height: 40, mr: 1.5 }} 
              />
            </Box>

            <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
              Sign in to manage your home improvement project.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                {...register('email')}
                margin="normal"
                fullWidth
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ mb: 2 }}
              />

              <TextField
                {...register('password')}
                margin="normal"
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{ mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  underline="hover"
                  sx={{ fontWeight: 500 }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  mb: 3
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{' '}
                  <Link
                    component={RouterLink}
                    to="/register"
                    variant="body2"
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    Sign up for free
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        
        {/* 3. Footer */}
        <Box sx={{ mt: 2, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          <Typography variant="caption" color="text.secondary">
            © {new Date().getFullYear()} Quorite. All rights reserved.
          </Typography>
        </Box>
      </Grid>

      {/* --- RIGHT SIDE: CAROUSEL & BRANDING --- */}
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2831&auto=format&fit=crop)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) =>
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Blue Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.85)', // Quorite Blue
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Box sx={{ maxWidth: 600, minHeight: 200 }}>
            {/* Animated Text Area */}
            {carouselSteps.map((step, index) => (
              <Fade in={activeStep === index} timeout={500} key={index}>
                <Box sx={{ display: activeStep === index ? 'block' : 'none' }}>
                  <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9, lineHeight: 1.6 }}>
                    "{step.description}"
                  </Typography>
                </Box>
              </Fade>
            ))}
            
            {/* Interactive Dots */}
            <Box sx={{ mt: 5, display: 'flex', gap: 1.5, justifyContent: 'center' }}>
               {carouselSteps.map((_, index) => (
                 <Box 
                   key={index}
                   onClick={() => setActiveStep(index)}
                   sx={{ 
                     width: 10, 
                     height: 10, 
                     borderRadius: '50%', 
                     bgcolor: 'white', 
                     opacity: activeStep === index ? 1 : 0.4,
                     cursor: 'pointer',
                     transition: 'all 0.3s ease',
                     '&:hover': { opacity: 0.8, transform: 'scale(1.2)' }
                   }} 
                 />
               ))}
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;